import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Divider,
  LinearProgress,
} from '@mui/material';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import PrintIcon from '@mui/icons-material/Print';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimerIcon from '@mui/icons-material/Timer';
import { MasaSiparisi, TrendyolOrder } from '../types';
import type { BlacklistCheckResult } from '../services/customerBlacklistService';
import BlacklistBanner from '../components/BlacklistBanner';

const formatDeliveryTimeTR = (utcDateStr: string): string => {
  const utcDate = utcDateStr.endsWith('Z') ? new Date(utcDateStr) : new Date(utcDateStr + 'Z');
  const trMs = utcDate.getTime() + 3 * 60 * 60 * 1000;
  const trDate = new Date(trMs);
  const hours = String(trDate.getUTCHours()).padStart(2, '0');
  const minutes = String(trDate.getUTCMinutes()).padStart(2, '0');
  const day = String(trDate.getUTCDate()).padStart(2, '0');
  const month = String(trDate.getUTCMonth() + 1).padStart(2, '0');
  const today = new Date();
  const todayTR = new Date(today.getTime() + 3 * 60 * 60 * 1000);
  const isToday = trDate.getUTCFullYear() === todayTR.getUTCFullYear() &&
    trDate.getUTCMonth() === todayTR.getUTCMonth() &&
    trDate.getUTCDate() === todayTR.getUTCDate();
  if (isToday) return `Bugün ${hours}:${minutes}`;
  return `${day}.${month} ${hours}:${minutes}`;
};

// ============================================================================
// NEW ORDER MODAL - Yeni Sipariş Bildirim Modal (QR/E-Ticaret/Kiosk/Getir/Migros/Trendyol)
// ============================================================================

export interface NewOrderModalProps {
  open: boolean;
  onClose: () => void;
  order: MasaSiparisi | null;
  trendyolOrder?: TrendyolOrder | null;
  autoAcceptSeconds?: number;
  onAutoAccept?: () => void;
  /**
   * Manuel "Onayla ve Yazdır" butonu — oto kabul ayarından bağımsız her zaman görünür.
   * Tıklandığında geri sayım iptal edilir (oto sistem ikinci kez yazdırmaz) ve
   * kabul + yazdırma hemen yapılır. Handler modal'ı kuyruk üzerinden kapatır.
   */
  onAcceptAndPrint?: () => void;
  /**
   * Kara liste sonucu. Kesin eşleşmede siyah bant çıkar, otomatik onay sayacı hiç başlamaz
   * ve "Onayla ve Yazdır" düğmesi "Yine de onayla ve yazdır" olur.
   */
  blacklist?: BlacklistCheckResult | null;
  /** Kara listedeki müşterinin siparişini reddet (platformun reddetme penceresini açar). */
  onBlacklistReject?: () => void;
  onOpenBlacklistEntry?: (entryId: number) => void;
}

const NewOrderModal: React.FC<NewOrderModalProps> = ({
  open,
  onClose,
  order,
  trendyolOrder,
  autoAcceptSeconds,
  onAutoAccept,
  onAcceptAndPrint,
  blacklist,
  onBlacklistReject,
  onOpenBlacklistEntry,
}) => {
  const { t } = useTranslation('salesSystem');
  const [countdown, setCountdown] = useState(-1);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const autoAcceptCalledRef = useRef(false);
  const countdownStartedRef = useRef(false);

  // Kabul+yazdırma işlemi sürüyor mu? (manuel butondan VEYA oto geri sayımdan)
  // true olduğunda her iki buton da kilitlenir — onay + fiş yazdırma birkaç
  // saniye sürdüğü için modal bu esnada açık kalıyor; kilit olmadan kasiyer
  // "olmadı" sanıp tekrar basınca çift onay + çift fiş oluşuyordu.
  // Reset gerekmez: sipariş kapanınca / yenisi gelince modal `key` ile remount
  // olur (PackageOrderModals `key={activeNewOrder?.key}`), state sıfırlanır.
  const [acceptAndPrintBusy, setAcceptAndPrintBusy] = useState(false);

  // Kara listedeki müşteride otomatik onay asla çalışmaz; kasiyer karar verir.
  const isBlacklisted = !!blacklist?.match;
  const totalSeconds = !isBlacklisted && autoAcceptSeconds && autoAcceptSeconds > 0 ? autoAcceptSeconds : 0;

  // O4 — Countdown fix:
  // Dependency listesine `order?.id` + `trendyolOrder?.id` eklendi. Sipariş
  // kuyruğundan yeni bir aktif sipariş geldiğinde (örn. ilk sipariş bittikten
  // sonra ikincisi promote edildi ve modal hâlâ açık kaldı — `open=true` aynı)
  // useEffect yeniden tetiklenip countdown sıfırdan başlasın. Aksi halde eski
  // sayım yeni sipariş üstüne devam eder ve yanlış sipariş için oto-onay ateşlenir.
  useEffect(() => {
    if (open && totalSeconds > 0) {
      console.log('[AutoAccept] Geri sayım başlatılıyor:', totalSeconds, 'saniye', { orderId: order?.id, trendyolOrderId: trendyolOrder?.id });
      autoAcceptCalledRef.current = false;
      countdownStartedRef.current = false;
      setCountdown(totalSeconds);
      const startTimer = setTimeout(() => {
        countdownStartedRef.current = true;
        timerRef.current = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              if (timerRef.current) clearInterval(timerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, 100);
      return () => {
        clearTimeout(startTimer);
        if (timerRef.current) clearInterval(timerRef.current);
      };
    } else {
      setCountdown(-1);
      countdownStartedRef.current = false;
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [open, totalSeconds, order?.id, trendyolOrder?.id]);

  useEffect(() => {
    if (countdown === 0 && countdownStartedRef.current && open && !autoAcceptCalledRef.current) {
      autoAcceptCalledRef.current = true;
      // Oto akış başlıyor — manuel "Onayla ve Yazdır" ve "Tamam" butonlarını
      // kilitle. Onay + yazdırma sürerken kasiyerin tekrar tetiklemesi çift
      // onay + çift fiş üretiyordu (ekran tarafında ayrıca key-bazlı guard var).
      setAcceptAndPrintBusy(true);
      console.log('[AutoAccept] Geri sayım bitti, oto kabul tetikleniyor...');
      onAutoAccept?.();
    }
  }, [countdown, open, onAutoAccept]);

  const handleManualClose = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    autoAcceptCalledRef.current = true;
    onClose();
  }, [onClose]);

  // Manuel "Onayla ve Yazdır": önce geri sayımı iptal et (oto sistem ikinci kez
  // yazdırmasın), sonra kabul + yazdırmayı hemen başlat. Modal, handler'ın sonunda
  // kuyruk üzerinden kapanır; o ana kadar butonlar kilitlenir (çift tıklama koruması).
  const handleAcceptAndPrint = useCallback(() => {
    if (acceptAndPrintBusy) return;
    setAcceptAndPrintBusy(true);
    if (timerRef.current) clearInterval(timerRef.current);
    autoAcceptCalledRef.current = true;
    setCountdown(-1); // geri sayım göstergesini gizle
    onAcceptAndPrint?.();
  }, [acceptAndPrintBusy, onAcceptAndPrint]);

  const isTrendyol = !!trendyolOrder && !order;

  const blacklistBanner = blacklist?.match || blacklist?.possible ? (
    <Box sx={{ mb: 2 }}>
      <BlacklistBanner result={blacklist} showAutoPaused={isBlacklisted && !!autoAcceptSeconds} onOpenEntry={onOpenBlacklistEntry} />
    </Box>
  ) : null;

  // Kara listede: kırmızı "Reddet" + koyu "Yine de onayla ve yazdır"
  const renderBlacklistActions = () => (
    <>
      {onBlacklistReject && (
        <Button variant="contained" color="error" size="large" fullWidth onClick={() => { if (timerRef.current) clearInterval(timerRef.current); autoAcceptCalledRef.current = true; onBlacklistReject(); }} disabled={acceptAndPrintBusy}>
          {t('orderManagement.blacklist.banner.reject')}
        </Button>
      )}
      <Button variant="outlined" color="inherit" size="large" fullWidth onClick={handleManualClose} disabled={acceptAndPrintBusy} startIcon={<VolumeOffIcon />}>
        {t('orderManagement.newOrderModal.ack')}
      </Button>
      {onAcceptAndPrint && (
        <Button variant="contained" size="large" fullWidth onClick={handleAcceptAndPrint} disabled={acceptAndPrintBusy} startIcon={<PrintIcon />}
          sx={{ bgcolor: '#1a1a1a', color: '#fff', '&:hover': { bgcolor: '#000' } }}>
          {acceptAndPrintBusy
            ? t('orderManagement.newOrderModal.acceptAndPrintBusy')
            : t('orderManagement.blacklist.banner.acceptAnyway')}
        </Button>
      )}
    </>
  );

  if (!order && !trendyolOrder) return null;
  
  const source = order?.siparisKaynagi?.toLowerCase() || '';
  const isGetir = !isTrendyol && source.includes('getir');
  const isMigros = !isTrendyol && source.includes('migros');
  const isYemeksepeti = !isTrendyol && source.includes('yemeksepeti');
  
  const getSourceIcon = () => {
    if (isTrendyol) {
      return (
        <img
          src="/assets/uber.png"
          alt="Uber Eats"
          style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8 }}
        />
      );
    }
    if (isGetir) {
      return (
        <img
          src="/assets/getirmemek.png"
          alt="Getir Yemek"
          style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8 }}
        />
      );
    }
    if (isMigros) {
      return (
        <img
          src="/assets/migrosyemek.svg"
          alt="Migros Yemek"
          style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8 }}
        />
      );
    }
    if (isYemeksepeti) {
      return (
        <img
          src="/assets/Yemeksepeti_logo.png"
          alt="Yemeksepeti"
          style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8 }}
        />
      );
    }
    if (source.includes('kiosk')) return <PointOfSaleIcon sx={{ fontSize: 30, color: 'white' }} />;
    if (source.includes('ecommerce')) return <ShoppingBagIcon sx={{ fontSize: 30, color: 'white' }} />;
    return <QrCode2Icon sx={{ fontSize: 30, color: 'white' }} />;
  };
  
  const getSourceName = () => {
    if (isTrendyol) return 'Uber Eats';
    if (isGetir) return 'Getir Yemek';
    if (isMigros) return 'Migros Yemek';
    if (isYemeksepeti) return 'Yemeksepeti';
    return order?.siparisKaynagi?.replace('_', ' ') || 'QR Menü';
  };

  const getBannerColor = () => {
    if (isTrendyol) return '#06C167';
    if (isGetir) return '#5D3EBC';
    if (isMigros) return '#FF6000';
    if (isYemeksepeti) return '#e31e24';
    return '#2e7d32';
  };

  if (isTrendyol && trendyolOrder) {
    const tOrder = trendyolOrder;
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            animation: 'shake 0.5s ease-in-out',
            '@keyframes shake': {
              '0%, 100%': { transform: 'translateX(0)' },
              '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
              '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' },
            },
          },
        }}
      >
        <Box sx={{ bgcolor: '#06C167', color: 'white', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 54, height: 54, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.9)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'pulse 1s infinite',
            '@keyframes pulse': { '0%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.1)' }, '100%': { transform: 'scale(1)' } },
          }}>
            {getSourceIcon()}
          </Box>
          <Box flex={1}>
            <Typography variant="h6" fontWeight="bold">{t('orderManagement.newOrderModal.title')}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{t('orderManagement.newOrderModal.platformOrder', { platform: 'Uber Eats' })}</Typography>
          </Box>
        </Box>

        <DialogContent sx={{ pt: 2 }}>
          <Box>
            {blacklistBanner}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Sipariş No</Typography>
              <Typography variant="h6" fontWeight="bold">#{tOrder.orderNumber}</Typography>
            </Box>

            {tOrder.customer && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Müşteri</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {tOrder.customer.firstName} {tOrder.customer.lastName}
                  </Typography>
                </Box>
              </Box>
            )}

            {tOrder.address?.phone && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Telefon</Typography>
                  <Typography variant="body1" fontWeight="medium">{tOrder.address.phone}</Typography>
                </Box>
              </Box>
            )}

            {tOrder.address && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <LocationOnIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">Adres</Typography>
                  <Typography variant="body1">
                    {[tOrder.address.neighborhood, tOrder.address.address1, tOrder.address.district, tOrder.address.city].filter(Boolean).join(', ')}
                  </Typography>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Sipariş Detayları ({tOrder.lines?.length || 0} ürün)
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
              {tOrder.lines?.map((line, index) => {
                const qty = line.items?.length || 1;
                const lineTotal = (line.unitSellingPrice ?? line.price ?? 0) * qty;
                return (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box>
                      <Typography variant="body2">{qty}x {line.name}</Typography>
                      {line.modifierProducts?.filter(m => m.price > 0).map((mod, mi) => (
                        <Typography key={mi} variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2 }}>
                          + {mod.name} (+{mod.price.toFixed(2)} ₺)
                        </Typography>
                      ))}
                    </Box>
                    <Typography variant="body2" fontWeight="medium">{lineTotal.toFixed(2)} ₺</Typography>
                  </Box>
                );
              })}
            </Box>

            {tOrder.customerNote && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body2" color="warning.dark" fontWeight="medium">
                  Not: {tOrder.customerNote}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">TOPLAM</Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {tOrder.totalPrice?.toFixed(2) || '0.00'} ₺
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
          {totalSeconds > 0 && countdown > 0 && (
            <Box sx={{ width: '100%', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                <TimerIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                <Typography variant="body2" color="warning.main" fontWeight="bold">
                  {countdown} saniye sonra otomatik işlenecek
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={((totalSeconds - countdown) / totalSeconds) * 100}
                sx={{ height: 6, borderRadius: 3 }}
                color="warning"
              />
            </Box>
          )}
          {isBlacklisted ? renderBlacklistActions() : (
            <>
          <Button variant="contained" color="success" size="large" fullWidth onClick={handleManualClose} disabled={acceptAndPrintBusy} startIcon={<VolumeOffIcon />}>
            {t('orderManagement.newOrderModal.ack')}
          </Button>
          {onAcceptAndPrint && (
            <Button variant="contained" color="primary" size="large" fullWidth onClick={handleAcceptAndPrint} disabled={acceptAndPrintBusy} startIcon={<PrintIcon />}>
              {acceptAndPrintBusy
                ? t('orderManagement.newOrderModal.acceptAndPrintBusy')
                : t('orderManagement.newOrderModal.acceptAndPrint')}
            </Button>
          )}
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  if (!order) return null;
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          animation: 'shake 0.5s ease-in-out',
          '@keyframes shake': {
            '0%, 100%': { transform: 'translateX(0)' },
            '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
            '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' },
          },
        },
      }}
    >
      <Box
        sx={{
          bgcolor: getBannerColor(),
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: '12px',
            bgcolor: 'rgba(255,255,255,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'pulse 1s infinite',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.1)' },
              '100%': { transform: 'scale(1)' },
            },
            ...( (!isGetir && !isMigros && !isYemeksepeti) && { bgcolor: 'rgba(255,255,255,0.2)' }),
          }}
        >
          {getSourceIcon()}
        </Box>
        <Box flex={1}>
          <Typography variant="h6" fontWeight="bold">
            {order?.isPreOrder ? t('orderManagement.newOrderModal.preOrderTitle') : t('orderManagement.newOrderModal.title')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {t('orderManagement.newOrderModal.platformOrder', { platform: getSourceName() })}
          </Typography>
        </Box>
      </Box>

      {order?.isPreOrder && order?.expectedDeliveryTime && (
        <Box sx={{
          bgcolor: '#ff6f00',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          animation: 'blink 1.5s ease-in-out infinite',
          '@keyframes blink': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.7 },
          },
        }}>
          <ScheduleIcon sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
              {t('orderManagement.newOrderModal.preOrderTitle')}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              Teslim: {formatDeliveryTimeTR(order.expectedDeliveryTime)}
            </Typography>
          </Box>
        </Box>
      )}

      <DialogContent sx={{ pt: 2 }}>
        <Box>
          {blacklistBanner}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Sipariş No
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              #{order.siparisId}
            </Typography>
          </Box>

          {/* Müşteri Bilgileri */}
          {(order.musteriAdi || order.kullaniciAdi) && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Müşteri
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {order.musteriAdi || order.kullaniciAdi}
                </Typography>
              </Box>
            </Box>
          )}

          {order.musteriTelefon && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Telefon
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {order.musteriTelefon}
                </Typography>
              </Box>
            </Box>
          )}

          {order.musteriAdres && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <LocationOnIcon color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Adres
                </Typography>
                <Typography variant="body1">
                  {order.musteriAdres}
                </Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Sipariş Detayları */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Sipariş Detayları ({order.siparisDetaylari?.length || 0} ürün)
          </Typography>
          <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
            {order.siparisDetaylari?.map((item, index) => (
              <Box 
                key={index} 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2">
                  {item.adet}x {item.urunAdi}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {item.toplamFiyat?.toFixed(2) || '0.00'} ₺
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Sipariş Notu */}
          {order.siparisNotu && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" color="warning.dark" fontWeight="medium">
                Not: {order.siparisNotu}
              </Typography>
            </Box>
          )}

          {/* Toplam */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">TOPLAM</Typography>
            <Typography variant="h5" fontWeight="bold" color="success.main">
              {order.toplamTutar?.toFixed(2) || '0.00'} ₺
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
        {totalSeconds > 0 && countdown > 0 && (
          <Box sx={{ width: '100%', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
              <TimerIcon sx={{ fontSize: 18, color: 'warning.main' }} />
              <Typography variant="body2" color="warning.main" fontWeight="bold">
                {countdown} saniye sonra otomatik işlenecek
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={((totalSeconds - countdown) / totalSeconds) * 100}
              sx={{ height: 6, borderRadius: 3 }}
              color="warning"
            />
          </Box>
        )}
        {isBlacklisted ? renderBlacklistActions() : (
          <>
        <Button
          variant="contained"
          color="success"
          size="large"
          fullWidth
          onClick={handleManualClose}
          disabled={acceptAndPrintBusy}
          startIcon={<VolumeOffIcon />}
        >
          {t('orderManagement.newOrderModal.ack')}
        </Button>
        {onAcceptAndPrint && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleAcceptAndPrint}
            disabled={acceptAndPrintBusy}
            startIcon={<PrintIcon />}
          >
            {acceptAndPrintBusy
              ? t('orderManagement.newOrderModal.acceptAndPrintBusy')
              : t('orderManagement.newOrderModal.acceptAndPrint')}
          </Button>
        )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NewOrderModal;
